import { memo, type ReactNode, type CSSProperties } from 'react';
import { Tooltip, TooltipProps } from '@mui/material';
import { Box, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import { HealthStatusKey } from '@core/utils/statusUtils.ts';

export interface TagState {
    bgcolor: string;
    color: string;
    darkBgcolor?: string;
    darkColor?: string;
    icon: ReactNode;
    label?: string;
    iconBg: boolean;
}

export const TAG_STATE = {
    success: {
        bgcolor: '#A0F5CE',
        color: '#144840',
        darkBgcolor: '#A3E8C7',
        darkColor: '#0F2521',
        icon: <i className="bi bi-info-circle" />,
        label: 'Healthy',
        iconBg: false
    },
    warning: {
        bgcolor: '#FFEBB5',
        color: '#5E371A',
        darkBgcolor: '#E8D39A',
        darkColor: '#38210F',
        icon: <i className="bi bi-info-circle" />,
        label: 'Check',
        iconBg: false
    },
    danger: {
        bgcolor: 'var(--color-field-error)',
        color: '#FFFFFF',
        darkBgcolor: 'var(--color-field-error)',
        darkColor: '#FFFFFF',
        icon: <i className="bi bi-info-circle" />,
        label: 'UnHealthy',
        iconBg: false
    },
    unknown: {
        bgcolor: '#D3E6F9',
        color: '#316074',
        darkBgcolor: '#C0CDDA',
        darkColor: '#285467',
        icon: <i className="bi bi-info-circle" />,
        label: 'Unknown',
        iconBg: false
    },
    inactive: {
        bgcolor: '#C4C4C4',
        color: '#000000',
        darkBgcolor: '#C3C3C3',
        darkColor: '#000000',
        icon: undefined,
        label: 'Closed',
        iconBg: false
    },
    info: {
        bgcolor: '#B0E3FA',
        color: 'var(--dark-blue-light)',
        darkBgcolor: '#A2CBDE',
        darkColor: '#003D58',
        icon: undefined,
        label: 'Unmanned',
        iconBg: false
    },
    offline: {
        bgcolor: '#EDC9FB',
        color: '#56206C',
        darkBgcolor: '#C8B3D0',
        darkColor: '#3C184B',
        icon: undefined,
        label: 'Out_Of_Service',
        iconBg: false
    },
    standby: {
        bgcolor: '#C5F0F1',
        color: '#0E5362',
        darkBgcolor: '#CFE6E6',
        darkColor: '#09353E',
        icon: undefined,
        label: 'Hybrid',
        iconBg: false
    },
    accent: {
        bgcolor: '#FFD9EB',
        color: '#800846',
        darkBgcolor: '#EBD7E1',
        darkColor: '#800846',
        icon: undefined,
        label: 'Staffed',
        iconBg: false
    }
} satisfies Record<string, TagState>;

export interface StatusTagConfig {
    label?: string;
    state: TagState;
    icon?: ReactNode;
    iconBg?: boolean;
}

interface StatusTagProps {
    config?: StatusTagConfig;
    label?: string;
    state?: TagState;
    icon?: ReactNode;
    iconBg?: boolean;
    className?: string;
    style?: CSSProperties;
    tooltip?: ReactNode;
    tooltipPlacement?: TooltipProps['placement'];
}

function StatusTagBase({
    config,
    label,
    state,
    icon,
    iconBg,
    className,
    style,
    tooltip,
    tooltipPlacement
}: StatusTagProps) {
    const resolvedState = state ?? config?.state ?? TAG_STATE.inactive;
    const resolvedLabel = label ?? config?.label ?? resolvedState.label;
    const resolvedIcon = icon ?? config?.icon ?? resolvedState.icon;
    const resolvedIconBg = iconBg ?? config?.iconBg ?? resolvedState.iconBg;

    const cssVars = {
        '--tag-bg': resolvedState.bgcolor,
        '--tag-dark-bg': resolvedState.darkBgcolor ?? resolvedState.bgcolor,
        '--tag-color': resolvedState.color,
        '--tag-dark-color': resolvedState.darkColor ?? resolvedState.color,
        '--tag-icon-bg': resolvedState.color,
        '--tag-icon-dark-bg': resolvedState.darkColor ?? resolvedState.color,
        '--tag-icon-color': resolvedState.bgcolor,
        '--tag-icon-dark-color': resolvedState.darkBgcolor ?? resolvedState.bgcolor,
        ...style
    } as CSSProperties;

    const tag = (
        <div className={['status-tag', className].filter(Boolean).join(' ')} style={cssVars}>
            <div className="status-tag__label-box">
                <span className="status-tag__label">{resolvedLabel}</span>
            </div>

            {resolvedIcon && (
                <div className="status-tag__icon-box">
                    {resolvedIconBg ? (
                        <div className="status-tag__icon--bg">{resolvedIcon}</div>
                    ) : (
                        <div className="status-tag__icon--plain">{resolvedIcon}</div>
                    )}
                </div>
            )}
        </div>
    );

    if (!tooltip) return tag;

    return (
        <Tooltip
            title={tooltip}
            placement={tooltipPlacement}
            arrow={true}
            componentsProps={{
                tooltip: {
                    className: 'status-tag__tooltip'
                }
            }}
        >
            <span style={{ display: 'inline-flex' }}>{tag}</span>
        </Tooltip>
    );
}

export const StatusTag = memo(StatusTagBase);
StatusTag.displayName = 'StatusTag';

export function defineStatusList<T extends Record<string, StatusTagConfig>>(map: T): T {
    return map;
}

export function customState(base: TagState, overrides: Partial<TagState>): TagState {
    return { ...base, ...overrides };
}

export function StatusTagList({
    configs,
    className,
    style
}: {
    configs: StatusTagConfig[];
    className?: string;
    style?: CSSProperties;
}) {
    return (
        <div className={['status-tag-list', className].filter(Boolean).join(' ')} style={style}>
            {configs.map((c) => (
                <StatusTag key={c.label ?? c.state.label} config={c} />
            ))}
        </div>
    );
}

export default StatusTag;

export const ShopStatus = defineStatusList({
    Closed: { state: TAG_STATE.inactive },
    Out_Of_Service: { state: TAG_STATE.offline },
    Unmanned: { state: TAG_STATE.info },
    Staffed: { state: TAG_STATE.accent },
    Hybrid: { state: TAG_STATE.standby }
});

export const HealthStatus = defineStatusList({
    Healthy: { state: TAG_STATE.success },
    Check: { state: TAG_STATE.warning },
    UnHealthy: { state: TAG_STATE.danger },
    Unknown: { state: TAG_STATE.unknown }
});

const DEVICE_TOOLTIP_CONFIG: Record<
    HealthStatusKey,
    {
        connectionOk: boolean;
        connectionKey: string;
        stateIcon: 'check' | 'error' | 'unknown';
        stateKey: string;
    }
> = {
    Healthy: {
        connectionOk: true,
        connectionKey: 'deviceTooltip.connection.connected',
        stateIcon: 'check',
        stateKey: 'deviceTooltip.state.healthy'
    },
    Check: {
        connectionOk: true,
        connectionKey: 'deviceTooltip.connection.connected',
        stateIcon: 'error',
        stateKey: 'deviceTooltip.state.check'
    },
    Unhealthy: {
        connectionOk: false,
        connectionKey: 'deviceTooltip.connection.deviceOffline',
        stateIcon: 'unknown',
        stateKey: 'deviceTooltip.state.unhealthy'
    },
    Unknown: {
        connectionOk: false,
        connectionKey: 'deviceTooltip.connection.gatewayOffline',
        stateIcon: 'unknown',
        stateKey: 'deviceTooltip.state.unknown'
    }
};

const STATE_ICON = {
    check: <i className="bi bi-check-circle-fill" />,
    error: <i className="bi bi-x-circle-fill" />,
    unknown: <i className="bi bi-question-circle-fill" />
};

export function buildPlainTooltip(message: string): ReactNode {
    return (
        <Box sx={{ px: 1.5, py: 1 }}>
            <Typography variant="body2">{message}</Typography>
        </Box>
    );
}

export function buildStateTooltip(name: string, healthStatus: string, t: TFunction, errorMessage?: string): ReactNode {
    const config = DEVICE_TOOLTIP_CONFIG[healthStatus as HealthStatusKey];
    if (!config) return null;

    return (
        <Box>
            {/* Title header */}
            <Box className="status-tag__header">
                <Typography fontSize={12} fontFamily="var(--font-family-medium)">
                    {name}
                </Typography>
            </Box>

            {/* Body */}
            <Box className="status-tag__body">
                <Typography fontSize={10} fontFamily="var(--font-family-medium)" display="block" sx={{ mb: 0.5 }}>
                    {t('deviceConnection')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.25 }}>
                    <span className="tooltip-icon">{config.connectionOk ? STATE_ICON.check : STATE_ICON.error}</span>
                    <Typography variant="caption">{t(config.connectionKey)}</Typography>
                </Box>

                <Typography fontFamily="var(--font-family-medium)" fontSize={10} display="block" sx={{ mb: 0.5 }}>
                    {t('deviceState')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <span className="tooltip-icon">{STATE_ICON[config.stateIcon]}</span>
                    <Typography variant="caption">
                        {t(config.stateKey, { error: errorMessage ?? 'unknown' })}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
